import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useRestaurant } from '@/features/restaurant/useRestaurant';
import type { ConfirmedReservation } from '@/types';
import { localParts, restaurantTz } from '@/lib/hours';

const WEEKDAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS_FR = [
  'jan', 'fév', 'mars', 'avr', 'mai', 'juin',
  'juil', 'août', 'sept', 'oct', 'nov', 'déc',
];

function formatDateFr(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`);
  return `${WEEKDAYS_FR[d.getDay()]} ${d.getDate().toString().padStart(2, '0')} ${MONTHS_FR[d.getMonth()]}`;
}

// SC-1081 — le hub renvoie l'instant dans SON fuseau (Paris) : on le relit a
// l'horloge du restaurant. Sans fiche restaurant (encore en chargement) ou sur
// une chaine illisible, on garde l'heure murale portee par la chaine.
function formatReservedFor(iso: string, tz: string | null): { dateFr: string; time: string } {
  const at = new Date(iso);
  if (tz && !Number.isNaN(at.getTime())) {
    const p = localParts(at, tz);
    const dd = p.day.toString().padStart(2, '0');
    return {
      // localParts: 0 = lundi ; WEEKDAYS_FR: 0 = dimanche.
      dateFr: `${WEEKDAYS_FR[(p.weekday + 1) % 7]} ${dd} ${MONTHS_FR[p.month - 1]}`,
      time: `${p.hh.toString().padStart(2, '0')}:${p.mm.toString().padStart(2, '0')}`,
    };
  }
  const dateFr = formatDateFr(iso.slice(0, 10)); // 'YYYY-MM-DD'
  const time = iso.slice(11, 16); // 'HH:mm' directly from the ISO string (local time portion)
  return { dateFr, time };
}

export default function ReservationConfirmationPage() {
  const { restaurantUuid = '' } = useParams<{
    restaurantUuid: string;
    reservationId: string;
  }>();
  const { t } = useTranslation();
  const location = useLocation();
  const reservation = (location.state as { reservation?: ConfirmedReservation } | null)?.reservation;
  const { identity } = useRestaurant(restaurantUuid);

  if (!reservation) {
    return <Navigate to={`/${restaurantUuid}`} replace />;
  }

  const { dateFr, time } = formatReservedFor(
    reservation.reservedFor,
    identity ? restaurantTz(identity) : null,
  );
  const persons = t('reservation.confirmation.personsCount', { count: reservation.partySize });

  return (
    <div className="max-w-[680px] mx-auto px-4 py-12 space-y-6">
      <style>{`
        @keyframes stampDrop {
          from { transform: translateY(-30px) rotate(-8deg) scale(1.2); opacity: 0; }
          to   { transform: translateY(0) rotate(0) scale(1); opacity: 1; }
        }
      `}</style>
      <div className="text-center">
        <div className="inline-block w-24 h-24 bg-mint border-2 border-ink shadow-stamp flex items-center justify-center mb-4 animate-[stampDrop_400ms_cubic-bezier(0.34,1.56,0.64,1)]">
          <span className="text-4xl">✓</span>
        </div>
        <h1 className="font-display text-3xl text-ink">
          {t('reservation.confirmation.title')}
        </h1>
        <p className="font-display text-2xl mt-2">{reservation.uuid}</p>
      </div>

      <div className="border-2 border-ink bg-paper p-5 shadow-stamp space-y-2 font-mono text-sm">
        <p className="font-display text-lg">
          {dateFr} · {time}
        </p>
        <p>
          {persons} · {t('reservation.confirmation.atName', { name: reservation.customerName })}
        </p>
      </div>

      {identity && (
        <div className="border-2 border-ink bg-paper p-5 shadow-stamp font-mono text-sm space-y-1">
          {identity.address && <p>📍 {identity.address}</p>}
          {identity.phone && (
            <p>
              📞{' '}
              <a href={`tel:${identity.phone.replace(/\s/g, '')}`} className="underline">
                {identity.phone}
              </a>
            </p>
          )}
        </div>
      )}

      <p className="font-mono text-sm text-center text-ink/70">
        {t('reservation.confirmation.statusNote')}
      </p>

      <p className="font-mono text-sm text-center text-ink/70">
        {identity?.phone ? (
          <Trans
            i18nKey="reservation.contactToModifyWithPhone"
            values={{ phone: identity.phone }}
            components={{
              tel: (
                <a
                  href={`tel:${identity.phone.replace(/\s/g, '')}`}
                  className="underline whitespace-nowrap"
                />
              ),
            }}
          />
        ) : (
          t('reservation.contactToModify')
        )}
      </p>

      <div className="flex flex-wrap gap-3 justify-center">
        <Link
          to={`/${restaurantUuid}`}
          className="bg-mint border-2 border-ink rounded-btn px-6 py-3 font-display shadow-stamp-sm"
        >
          {t('reservation.backToMenu')}
        </Link>
      </div>
    </div>
  );
}
